// Human names for the things Oreia tracks.
//
// Internally a pattern is a canonical id — `theme:autonomy_freedom`, `belief:not_enough` — which is
// the right shape for scoring and exactly the wrong shape to show someone. Title-casing the id
// gets you "Autonomy Freedom" and "Not Enough": still the schema, just wearing capital letters.
//
// So the vocabulary gets written names. Beliefs are phrased the way a person says them to
// themselves, in the first person, because that is what a belief is. Compound themes get the "&"
// they are missing. Sentence case throughout — a heading, not a shout.
//
// The map is deliberately explicit rather than clever: there are about sixty of these, they change
// rarely, and every guess a rule makes is a sentence someone reads about their own life.

const NAMES: Record<string, string> = {
  // Themes
  'theme:autonomy_freedom': 'Autonomy & freedom',
  'theme:belonging': 'Belonging',
  'theme:boundaries': 'Boundaries',
  'theme:caretaking': 'Caretaking',
  'theme:control_vs_surrender': 'Control & surrender',
  'theme:fear_of_abandonment': 'Fear of abandonment',
  'theme:fear_of_failure': 'Fear of failure',
  'theme:grief_loss': 'Grief & loss',
  'theme:healing': 'Healing',
  'theme:inner_critic': 'The inner critic',
  'theme:intimacy': 'Intimacy',
  'theme:perfectionism': 'Perfectionism',
  'theme:purpose_meaning': 'Purpose & meaning',
  'theme:recognition': 'Recognition',
  'theme:resilience_growth': 'Resilience & growth',
  'theme:safety_security': 'Safety & security',
  'theme:seeking_acceptance': 'Seeking acceptance',
  'theme:self_worth': 'Self-worth',
  'theme:shame_hiding': 'Shame & hiding',
  'theme:transformation': 'Transformation',
  'theme:trust_repair': 'Repairing trust',

  // Beliefs, in the voice they are actually held in.
  'belief:all_or_nothing': 'All or nothing',
  'belief:cant_trust': "I can't rely on anyone",
  'belief:must_be_perfect': 'I have to be perfect',
  'belief:not_enough': "I'm not enough",
  'belief:powerless': 'There is nothing I can do',
  'belief:responsible_for_others': "I'm responsible for everyone",
  'belief:stopping_is_failing': 'Stopping means failing',
  'belief:unlovable': "I'm hard to love",

  // Desires
  'desire:connection': 'Connection',
  'desire:freedom': 'Freedom',
  'desire:love': 'Love',
  'desire:peace': 'Peace',
  'desire:recognition': 'Recognition',
  'desire:safety': 'Safety',
  'desire:success': 'Success',
};

/**
 * The name to show for a canonical id.
 *
 * Anything outside the written vocabulary — a symbol drawn from someone's own dreams, a new
 * emotion — falls back to sentence case, which is right for the single words those tend to be
 * ("Longing", "Folding staircase") and is at worst unremarkable for the rest.
 */
export function patternLabel(canonicalId: string): string {
  const known = NAMES[canonicalId];
  if (known) return known;

  const raw = canonicalId.includes(':')
    ? canonicalId.slice(canonicalId.indexOf(':') + 1)
    : canonicalId;

  const words = raw.split(/[_\-\s]+/).filter(Boolean).join(' ').toLowerCase();
  if (!words) return '';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The same thing, named so it can sit inside a sentence.
 *
 * `patternLabel` writes a heading, and a heading dropped mid-sentence reads wrong in two different
 * ways depending on what kind of thing it names. A theme is a noun and only needs its capital
 * letter back ("the inner critic"). A symbol is one of a person's own images and wants an article
 * ("the folding staircase"). A belief is not a noun at all — it is a whole first-person sentence,
 * so it is quoted and introduced rather than lowercased, because "you wrote about i'm not enough"
 * is not something to show anyone.
 */
export function subjectPhrase(canonicalId: string): string {
  const label = patternLabel(canonicalId);
  if (!label) return '';

  const namespace = canonicalId.includes(':') ? canonicalId.slice(0, canonicalId.indexOf(':')) : '';

  if (namespace === 'belief') return `the belief \u201c${label}\u201d`;

  const lower = label.charAt(0).toLowerCase() + label.slice(1);
  if (namespace === 'symbol') return lower.startsWith('the ') ? lower : `the ${lower}`;
  return lower;
}
